import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ProposalTemplateService } from '@/pages/quotation/proposal-template.service';
import { CatalogService } from '@/pages/catalog/catalog.service';
import { ProposalFieldsEditorComponent } from '@/pages/quotation/proposal-fields-editor.component';
import { AppDatePipe } from '@/pages/service/core/app-date.pipe';

/**
 * Quotation → Proposal templates configuration (Settings). Upload designed proposals, mark
 * ONE active (used automatically when an agent generates a proposal), delete, and configure
 * each template's {{field:key}} inputs. Moved here from the quotation panel so the customer
 * flow stays clean — the agent just prints and the active template is applied automatically.
 */
@Component({
  selector: 'app-proposal-templates',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, DialogModule, InputTextModule, ToastModule, TooltipModule, AppDatePipe, ProposalFieldsEditorComponent],
  providers: [MessageService],
  template: `
<p-toast />
<div class="pt">
  <div class="pt__head">
    <div>
      <h3>{{ kind === 'document' ? 'Document templates' : 'Proposal templates' }}</h3>
      <p class="pt__hint" *ngIf="kind !== 'document'">Upload your designed proposal (a <b>ZIP</b>, an <b>.html</b> plus images, or a fillable <b>.pdf</b>). Mark one <b>Active</b> — it's applied automatically when an agent generates a proposal from a quotation. Placeholders: <code>{{ '{{QUOTATION}}' }}</code> · <code>{{ '{{COMPONENTS}}' }}</code> · <code>{{ '{{field:key}}' }}</code>.</p>
      <p class="pt__hint" *ngIf="kind === 'document'">Upload an agreement, sale deed or warranty letter — a fillable <b>.pdf</b> (its form fields are filled by name), or an <b>.html</b>/<b>ZIP</b> design. Then map its fields, and add a <b>Generate</b> button on the journey phase that should produce it. Placeholders: <code>{{ '{{field:key}}' }}</code> · <code>{{ '{{DOC_NO}}' }}</code> · <code>{{ '{{CUSTOMER_NAME}}' }}</code>. Your letterhead stays part of the uploaded file.</p>
    </div>
  </div>

  <!-- One screen, two lists. A quotation template and an agreement have nothing to do with each
       other, and the phase pickers filter by the same kind — so switching here switches both. -->
  <div class="pt-kinds">
    <button type="button" class="pt-kind" [class.is-on]="kind === 'quotation'" (click)="setKind('quotation')">
      <i class="pi pi-file"></i> Quotation templates
    </button>
    <button type="button" class="pt-kind" [class.is-on]="kind === 'document'" (click)="setKind('document')">
      <i class="pi pi-file-edit"></i> Document templates
    </button>
  </div>

  <div class="pt-up">
    <input type="text" pInputText [placeholder]="kind === 'document' ? 'Document name (e.g. Sale Agreement)' : 'Template name (e.g. Residential Solar Proposal)'" [(ngModel)]="tplName" />
    <input type="file" multiple accept=".zip,.html,.htm,.pdf,image/*" (change)="onFiles($event)" />
    <input *ngIf="kind === 'document'" type="text" pInputText class="pt-numfmt"
           placeholder="Number format (optional) — AGR/{{ '{YYYY}' }}/{{ '{SEQ:4}' }}" [(ngModel)]="tplNumberFormat"
           pTooltip="Fills the {{ '{{DOC_NO}}' }} placeholder. Tokens: {{ '{YYYY}' }} {{ '{YY}' }} {{ '{MM}' }} {{ '{DD}' }} {{ '{SEQ}' }} {{ '{SEQ:4}' }} (zero-padded). Leave blank for no numbering."
           tooltipPosition="top" />
    <p-button [label]="tplSaving ? 'Uploading…' : 'Upload'" icon="pi pi-upload" [disabled]="tplSaving" (onClick)="upload()" />
  </div>

  <div class="pt-list">
    <div *ngIf="loading" class="pt-muted pad">Loading…</div>
    <div *ngIf="!loading && !templates.length" class="pt-muted pad">No templates yet.</div>
    <div class="pt-row" *ngFor="let t of templates" [class.pt-row--active]="t.active">
      <button class="pt-active" *ngIf="kind !== 'document'" [class.on]="t.active" (click)="setActive(t)"
              pTooltip="{{ t.active ? 'Active — used automatically' : 'Make this the active template' }}" tooltipPosition="top">
        <i class="pi" [ngClass]="t.active ? 'pi-check-circle' : 'pi-circle'"></i>
      </button>
      <div class="pt-row__name">
        <i class="pi pi-images"></i> <b>{{ t.name }}</b>
        <span class="pt-badge" *ngIf="t.active">Active</span>
        <span class="pt-muted sm" *ngIf="t.updatedAt">· {{ t.updatedAt | appDate:'datetime' }}</span>
      </div>
      <div class="pt-row__components" *ngIf="kind !== 'document'">
        <label class="pt-muted sm">Components</label>
        <select class="pt-fsel pt-csel"
                [ngModel]="t.componentsCatalogId ?? null"
                (ngModelChange)="onComponentsCatalogChange(t, $event)"
                pTooltip="Components table source — blank = the agent's quoted items; pick a catalogue to list that catalogue's items"
                tooltipPosition="top">
          <option *ngFor="let o of catalogOptions" [ngValue]="o.value">{{ o.label }}</option>
        </select>
      </div>
      <!-- Who must sign. 'None' keeps a document generate-and-file only; the others make signing
           due the moment the document is locked. -->
      <div class="pt-row__components" *ngIf="kind === 'document'">
        <label class="pt-muted sm">Signed by</label>
        <select class="pt-fsel"
                [ngModel]="t.signers || 'none'"
                (ngModelChange)="onSignersChange(t, $event)"
                pTooltip="Who has to sign a document generated from this template. Signing becomes due when the document is locked."
                tooltipPosition="top">
          <option *ngFor="let o of signerOptions" [ngValue]="o.value">{{ o.label }}</option>
        </select>
      </div>
      <div class="pt-row__components" *ngIf="kind === 'document'">
        <label class="pt-muted sm">Number</label>
        <input type="text" pInputText class="pt-fsel pt-numfmt"
               [ngModel]="t.docNumberFormat || ''" (ngModelChange)="onNumberFormatChange(t, $event)"
               placeholder="none"
               pTooltip="Pattern for {{ '{{DOC_NO}}' }} — e.g. AGR/{{ '{YYYY}' }}/{{ '{SEQ:4}' }}. Blank = this document is not numbered."
               tooltipPosition="top" />
      </div>
      <span class="pt-row__acts">
        <p-button label="Fields" icon="pi pi-sliders-h" size="small" [text]="true" (onClick)="openFields(t)" pTooltip="Configure the field inputs this template uses" tooltipPosition="top" />
        <p-button icon="pi pi-trash" size="small" [text]="true" severity="danger" (onClick)="remove(t)" />
      </span>
    </div>
  </div>
</div>

<!-- Per-template {{field:key}} config — the field-mapping table is the reusable editor. -->
<p-dialog [(visible)]="fieldsOpen" [header]="'Proposal fields — ' + (fieldsTpl?.name || '')" [modal]="true" [style]="{width:'78rem', maxWidth:'96vw'}" [contentStyle]="{maxHeight:'80vh', overflow:'auto'}">
  <app-proposal-fields-editor [templateId]="fieldsTpl?.id" [capturedOptions]="[]" [catalogGroups]="catalogGroups" (saved)="fieldsOpen=false"></app-proposal-fields-editor>
</p-dialog>
  `,
  styles: [`
    .pt { display:flex; flex-direction:column; gap:14px; }
    .pt__head h3 { margin:0; font-size:var(--gh-fs-19); }
    .pt__hint { margin:4px 0 0; color:var(--gh-text-3); font-size:var(--gh-fs-13); max-width:60rem; }
    .pt-kinds { display:flex; gap:6px; margin:14px 0 10px; }
    .pt-kind { display:inline-flex; align-items:center; gap:7px; padding:7px 14px; border:1px solid var(--gh-border);
               border-radius:var(--gh-r-pill); background:var(--gh-surface); color:var(--gh-text-2);
               font:inherit; font-size:var(--gh-fs-13); font-weight:600; cursor:pointer; }
    .pt-kind:hover { background:var(--gh-surface-sunken); }
    .pt-kind.is-on { border-color:var(--gh-primary-bright); color:var(--gh-primary); background:var(--gh-primary-wash); font-weight:700; }
    .pt-numfmt { min-width:15rem; }
    .pt__hint code { background:var(--gh-surface-hover); padding:1px 5px; border-radius:4px; font-size:var(--gh-fs-12); }
    .pt-up { display:flex; flex-wrap:wrap; gap:.6rem; align-items:center; }
    /* pInputText: the --p-* bridge in styles.scss already gives it the .gh-input
       box, so the border / radius / padding that used to be restated here are
       gone and only how it fills the upload row is left. */
    .pt-up input[type=text] { flex:1 1 18rem; min-width:12rem; }
    .pt-list { display:flex; flex-direction:column; gap:.5rem; }
    .pt-muted { color:var(--gh-text-muted); } .sm { font-size:var(--gh-fs-13); } .pad { padding:10px 2px; }
    .pt-row { display:flex; align-items:center; gap:10px; border:1px solid var(--gh-border); border-radius:var(--gh-r-md); padding:.6rem .8rem; background:var(--gh-surface); }
    .pt-row--active { border-color:var(--gh-primary-300); background:var(--gh-primary-wash); }
    .pt-active { background:none; border:none; cursor:pointer; color:var(--gh-text-muted); font-size:var(--gh-fs-19); padding:0; }
    .pt-active.on { color:var(--gh-primary); }
    .pt-row__name { flex:1; min-width:0; display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
    .pt-row__name i { color: var(--primary-color); }
    .pt-badge { font-size:var(--gh-fs-11); font-weight:700; text-transform:uppercase; letter-spacing:.04em; padding:.1rem .45rem; border-radius:var(--gh-r-pill); background:var(--gh-primary-100); color:var(--gh-primary); }
    .pt-row__acts { display:flex; align-items:center; gap:2px; margin-left:auto; }
    /* Field grid — the Leads table treatment, kept in step with the live copy in
       proposal-fields-editor.component.ts, which is the component that actually
       renders this table now (these rules are view-encapsulated to THIS template,
       so they no longer reach it — they stay only so the two don't drift apart).
       The table IS the card: separate + zero spacing, because collapse
       defeats the corner clip and drops the border off any sticky cell. */
    .pt-fields {
      width:100%;
      border-collapse:separate; border-spacing:0;
      font-size:var(--gh-fs-12);
      margin:6px 0 10px;
      background:var(--gh-surface);
      border:1px solid var(--gh-border);
      border-radius:var(--gh-r-lg);
      overflow:hidden;
    }
    .pt-fields th {
      text-align:left;
      font-family: var(--gh-font-ui);
      font-size: var(--gh-fs-11);
      text-transform: uppercase;
      color: var(--gh-text-2);
      height:38px;
      padding:0 14px;
      background: var(--gh-surface-sunken);
      border-bottom:1px solid var(--gh-border);
      font-weight: 800;
      letter-spacing: 0.08em;
      white-space:nowrap;
    }
    /* 40px is the canonical row. A table cell treats height as a floor, not a
       cap, so a row whose Value cell opens the formula inserters or the whole
       cat-token picker still grows to fit instead of clipping. */
    .pt-fields td {
      height:40px;
      padding:5px 14px;
      border-bottom:1px solid var(--gh-divider);
      color:var(--gh-text);
      font-weight:500;
      vertical-align:middle;
      font-variant-numeric:tabular-nums;
    }
    .pt-fields tbody tr { transition:background var(--gh-t-fast); }
    /* Neutral hover, not the brand wash: these rows are filled in place, they
       don't open anything, so they must not read as clickable. */
    .pt-fields tbody tr:hover { background:var(--gh-surface-sunken); }
    .pt-fields tbody tr:last-child td { border-bottom:none; }
    .pt-fields td.c, .pt-fields th.c { text-align:center; }
    /* ── Compact field paint ────────────────────────────────────────────
       .pt-fsel is live in the Components cell of every template row; the
       other two belong to the mirrored field grid above. All three are the
       COMPACT step of the canonical field, not the 34px one: the grid packs
       a select, an input and a textarea into a 40px row, and .pt-fsel here
       sits beside size="small" buttons at the same scale. Bare <select> and
       <textarea> carry no PrimeNG directive, so the --p-* bridge cannot
       reach them at all -- the border, fill, corner, placeholder and focus
       ring have to be the canonical ones by hand, and the pInputText is
       pinned alongside them so one row cannot show two control heights. */
    .pt-fields input[pInputText], .pt-fsel, .pt-opts {
      width: 100%;
      padding: 0 12px;
      border: 1px solid var(--gh-border);
      border-radius: var(--gh-r-sm);
      background: var(--gh-surface);
      color: var(--gh-text);
      font: inherit;
      font-size: var(--gh-fs-13);
      transition: border-color var(--gh-t-fast), box-shadow var(--gh-t-fast);
    }
    .pt-fields input[pInputText]::placeholder, .pt-opts::placeholder { color:var(--gh-text-muted); }
    .pt-fields input[pInputText]:focus, .pt-fsel:focus, .pt-opts:focus {
      outline:none; border-color:var(--gh-primary-bright); box-shadow:var(--gh-ring);
    }
    .pt-fields input[pInputText]:disabled, .pt-fsel:disabled, .pt-opts:disabled {
      background:var(--gh-neutral-100); color:var(--gh-text-muted);
    }
    /* The options editor is a continuation of the dropdown row above it, not a
       row in its own right, so it drops the top inset and sits tight under it. */
    .pt-optsrow td { padding-top:0; }
    .pt-opts { resize:vertical; }
    .pt-fields .x { background:none; border:none; color:var(--gh-danger); cursor:pointer; }
    .pt-hint { color:var(--gh-text-muted); font-size:var(--gh-fs-12); margin-top:3px; }
    .pt-fieldacts { display:flex; flex-wrap:wrap; gap:.4rem; align-items:center; }
    .pt-row__components { display:flex; align-items:center; gap:6px; }
    .pt-csel { width:auto; min-width:13rem; max-width:20rem; }
  `],
})
export class ProposalTemplatesComponent implements OnInit {
  templates: any[] = [];
  loading = false;
  tplName = '';
  tplFiles: File[] = [];
  tplSaving = false;
  /** Which list this screen is showing — quotation templates or document templates. */
  kind: 'quotation' | 'document' = 'quotation';
  /** Number format for the document about to be uploaded (documents only). */
  tplNumberFormat = '';

  /** Catalogue picker for the per-template "Components" source. Leading option = the quote's items. */
  catalogOptions: { label: string; value: string | null }[] = [{ label: "From the quote's items (default)", value: null }];

  fieldsOpen = false;
  fieldsTpl: { id: string; name: string } | null = null;

  /** Catalogue-column grouped picklist (catalogue › level → columns), passed to the
   *  reusable fields editor so its "Catalogue" source can be picked from a dropdown. */
  catalogGroups: { label: string; items: { label: string; value: string }[] }[] = [];

  constructor(private tpl: ProposalTemplateService, private catalogSvc: CatalogService, private toast: MessageService) {}

  ngOnInit(): void { this.load(); this.loadCatalogs(); this.loadCatalogGroups(); }

  load(): void {
    this.loading = true;
    // Filtered server-side by kind: the two lists never bleed into each other, and a company with
    // many quotation templates does not have to scroll past them to find an agreement.
    this.tpl.listByKind(this.kind).subscribe({
      next: (res: any) => { this.templates = res?.data ?? res ?? []; this.loading = false; },
      error: () => { this.templates = []; this.loading = false; },
    });
  }

  /** Switch list. Clears the upload row so a name typed for one kind cannot land in the other. */
  setKind(k: 'quotation' | 'document'): void {
    if (this.kind === k) return;
    this.kind = k;
    this.tplName = '';
    this.tplFiles = [];
    this.tplNumberFormat = '';
    this.load();
  }

  /** Who must sign a document made from this template. */
  readonly signerOptions = [
    { label: 'No signature', value: 'none' },
    { label: 'Agent only', value: 'agent' },
    { label: 'Customer only', value: 'customer' },
    { label: 'Agent + customer', value: 'both' },
  ];

  /** Save the signer rule. Kept as its own call so a mis-typed number format can never take the
   *  signing rule down with it. */
  onSignersChange(t: any, value: string): void {
    const prev = t.signers;
    t.signers = value;
    this.tpl.setDocumentMeta(t.id, 'document', t.docNumberFormat || null, value as any).subscribe({
      next: () => this.toast.add({ severity: 'success', summary: 'Signing rule saved' }),
      error: () => {
        t.signers = prev;   // put the row back so the screen never shows an unsaved value
        this.toast.add({ severity: 'error', summary: 'Could not save the signing rule' });
      },
    });
  }

  /** Save a document row's number format (blank clears numbering). */
  onNumberFormatChange(t: any, value: string): void {
    const fmt = (value || '').trim();
    t.docNumberFormat = fmt;
    this.tpl.setDocumentMeta(t.id, 'document', fmt || null, t.signers || null).subscribe({
      next: () => {},
      error: () => this.toast.add({ severity: 'error', summary: 'Could not save the number format' }),
    });
  }

  onFiles(ev: Event): void { this.tplFiles = Array.from((ev.target as HTMLInputElement).files || []); }

  upload(): void {
    if (!this.tplName.trim()) { this.toast.add({ severity: 'warn', summary: 'Name the template' }); return; }
    if (!this.tplFiles.length) { this.toast.add({ severity: 'warn', summary: 'Choose a PDF, a ZIP, or an .html plus images' }); return; }
    this.tplSaving = true;
    const fmt = this.tplNumberFormat.trim();
    this.tpl.create(this.tplName.trim(), this.tplFiles, this.kind).subscribe({
      next: (res: any) => {
        const created = res?.data ?? res;
        // The number format is a separate call: create() is multipart, and squeezing an optional
        // text field into it would mean touching the quotation upload path too.
        if (this.kind === 'document' && fmt && created?.id) {
          this.tpl.setDocumentMeta(created.id, 'document', fmt).subscribe({ next: () => this.load(), error: () => this.load() });
        }
        this.tplSaving = false; this.tplName = ''; this.tplFiles = []; this.tplNumberFormat = '';
        this.load(); this.toast.add({ severity: 'success', summary: this.kind === 'document' ? 'Document template saved' : 'Template saved' });
      },
      error: (e: any) => { this.tplSaving = false; this.toast.add({ severity: 'error', summary: 'Upload failed', detail: e?.message }); },
    });
  }

  remove(t: any): void {
    if (!confirm(`Delete proposal template “${t.name}”?`)) return;
    this.tpl.remove(t.id).subscribe({
      next: () => { this.toast.add({ severity: 'success', summary: 'Template deleted' }); this.load(); },
      error: () => this.toast.add({ severity: 'error', summary: 'Delete failed' }),
    });
  }

  setActive(t: any): void {
    if (t.active) return;
    this.tpl.setActive(t.id).subscribe({
      next: () => { this.templates.forEach(x => x.active = x.id === t.id); this.toast.add({ severity: 'success', summary: `“${t.name}” is now active` }); },
      error: () => this.toast.add({ severity: 'error', summary: 'Could not set active' }),
    });
  }

  /** Load the company's catalogs for the Components picker (leading option = the quote's own items). */
  loadCatalogs(): void {
    this.catalogSvc.listCatalogs().subscribe({
      next: (res: any) => {
        const list = (res?.data ?? res ?? []) as any[];
        this.catalogOptions = [
          { label: "From the quote's items (default)", value: null },
          ...list.map(c => ({ label: c.name, value: c.id })),
        ];
      },
      error: () => { /* keep the default-only option */ },
    });
  }

  /** Persist the template's Components source — null clears it back to the quote's own items. */
  onComponentsCatalogChange(t: any, catalogId: string | null): void {
    const prev = t.componentsCatalogId ?? null;
    const next = catalogId ?? null;
    if (prev === next) return;
    t.componentsCatalogId = next;                 // optimistic — keeps the select in sync
    this.tpl.setComponentsCatalog(t.id, next).subscribe({
      next: () => this.toast.add({ severity: 'success', summary: next ? 'Components use the chosen catalogue' : 'Components use the quote items' }),
      error: (e: any) => { t.componentsCatalogId = prev; this.toast.add({ severity: 'error', summary: 'Could not update components source', detail: e?.message }); },
    });
  }

  /** Open the fields dialog for a template — the reusable editor loads/saves the rows
   *  itself off the [templateId] input, so this just points it at the chosen template. */
  openFields(t: any): void {
    this.fieldsTpl = t;
    this.fieldsOpen = true;
  }

  /** Load the company's catalogs (with their levels+columns) into the grouped picklist the
   *  fields editor uses for its "Catalogue" source. Each group = one catalogue › level;
   *  each item = a column. Per-catalogue fetch errors are tolerated (that catalogue skipped). */
  private loadCatalogGroups(): void {
    this.catalogSvc.listCatalogs().subscribe({
      next: (cats) => {
        const list = Array.isArray(cats) ? cats : [];
        if (!list.length) { this.catalogGroups = []; return; }
        forkJoin(list.map(c => this.catalogSvc.getCatalog(c.id).pipe(catchError(() => of(null)))))
          .subscribe(results => {
            const groups: { label: string; items: { label: string; value: string }[] }[] = [];
            for (const cat of results) {
              if (!cat) continue;
              for (const lvl of (cat.levels || [])) {
                const items = (lvl.columns || []).map(col => ({ label: col.displayName || col.columnKey, value: col.columnKey }));
                groups.push({ label: `${cat.name} › ${lvl.name}`, items });
              }
            }
            this.catalogGroups = groups;
          });
      },
      error: () => { this.catalogGroups = []; },
    });
  }
}
